import React, { useState, useRef, useEffect } from 'react';
import {
  Sparkle,
  Image as ImageIcon,
  Trash,
  ArrowsClockwise,
  DownloadSimple,
  X,
  PaperclipHorizontal,
  Question,
} from '@phosphor-icons/react';
import showToast from '@Common/toast/showToast';
import { imageGenerationAPI } from '@Lib/axios/image-generation';
import { useAuth } from '@Context/CognitoAuthProvider';
import { GeneratedImage, ImageGenerationResponse } from '@Models/types';
import ConfirmationModal from '@Common/ConfirmationModal';
import FileAttachmentDisplay from '@Common/FileAttachmentDisplay';
import PromptInstructions from './PromptInstructions';
import MaskModalInput from './MaskModalInput';
import {
  UploadResult,
  handleFileSelectionUpload,
  removeAttachmentAtIndex,
  clearAllAttachments,
  createMessageWithAttachments,
  validatePromptBeforeSend,
} from '@Utils/FileAttachmentUtils';

const ImageGenerator: React.FC = () => {
  const { logout } = useAuth();
  const api = imageGenerationAPI(logout);

  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(
    null
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isFetchingImages, setIsFetchingImages] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<GeneratedImage | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [attachments, setAttachments] = useState<UploadResult[]>([]);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isMaskModalOpen, setIsMaskModalOpen] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      if (prompt) {
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  }, [prompt]);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup abort controller on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  const formatDate = (d: Date) =>
    d.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const loadHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const response = await api.getHistory(50, 0);
      const images: GeneratedImage[] = response.history.map((item) => ({
        id: item.id,
        url: '',
        prompt: item.prompt,
        timestamp: new Date(item.timestamp),
        analytics: item.analytics,
      }));

      setGeneratedImages(images);
    } catch (error) {
      console.error('Failed to load history:', error);
      showToast.error('Failed to load image history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleGenerate = async () => {
    if (!validatePromptBeforeSend(prompt.trim(), attachments)) {
      return;
    }

    setIsGenerating(true);

    try {
      let finalPrompt = prompt.trim();

      // Create a clean copy of attachments for this generation
      // Ensure undefined if empty, not empty array
      const currentAttachments =
        attachments.length > 0 ? [...attachments] : undefined;

      if (currentAttachments) {
        finalPrompt = createMessageWithAttachments(
          prompt.trim(),
          currentAttachments
        );
      }

      const response = await api.generateImage({
        prompt: finalPrompt,
        numberOfImages: 1,
        quality: 'standard',
        ...(currentAttachments && { attachments: currentAttachments }),
      });

      if (response.maskRequired) {
        setIsMaskModalOpen(true);
        setIsGenerating(false);
        return;
      }

      await handleGenerationResponse(response, finalPrompt);
    } catch (error) {
      console.error('Generation error:', error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      showToast.error(errorMessage);

      clearAllAttachments(setAttachments);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const performGeneration = async (maskPrompt: string | null) => {
    setIsGenerating(true);

    try {
      let finalPrompt = prompt.trim();

      const currentAttachments =
        attachments.length > 0 ? [...attachments] : undefined;

      if (currentAttachments) {
        finalPrompt = createMessageWithAttachments(
          prompt.trim(),
          currentAttachments
        );
      }

      const response = await api.generateImage({
        prompt: finalPrompt,
        numberOfImages: 1,
        quality: 'standard',
        ...(currentAttachments && { attachments: currentAttachments }),
        ...(maskPrompt && { maskPrompt }),
      });

      if (response.maskRequired) {
        showToast.error(
          'Unable to process the mask. Please try a different description.'
        );
        setIsMaskModalOpen(true);
        setIsGenerating(false);
        return;
      }

      await handleGenerationResponse(response, finalPrompt);
    } catch (error) {
      console.error('Generation error:', error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);

      if (
        errorMessage.toLowerCase().includes('mask') ||
        errorMessage.toLowerCase().includes('cannot identify')
      ) {
        showToast.error(
          "I'm sorry, the model cannot identify the mask subject. Please try again or use another image."
        );
      } else {
        showToast.error(errorMessage);
      }

      clearAllAttachments(setAttachments);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerationResponse = async (
    response: ImageGenerationResponse,
    finalPrompt: string
  ) => {
    if (response.jobId && response.status === 'processing') {
      const pollInterval = 2000;
      const maxAttempts = 30;
      let attempts = 0;

      const pollJob = async (): Promise<void> => {
        attempts++;

        if (attempts > maxAttempts) {
          throw new Error('Image generation timed out. Please try again.');
        }

        const jobStatus = await api.getJobStatus(response.jobId!);

        if (jobStatus.status === 'completed') {
          const imgs = Array.isArray(jobStatus.images) ? jobStatus.images : [];

          if (imgs.length === 0) {
            throw new Error('No images were generated');
          }

          const newImages: GeneratedImage[] = imgs.map(
            (imageUrl: string, idx: number) => ({
              id: `${jobStatus.refId}-${idx}`,
              url: imageUrl,
              prompt: finalPrompt,
              timestamp: new Date(),
              analytics: jobStatus.analytics,
              fileName:
                attachments.length > 0
                  ? attachments.map((a) => a.fileName)
                  : undefined,
            })
          );

          setGeneratedImages((prev) => [...newImages, ...prev]);
          setSelectedImage(newImages[0]);
          setPrompt('');
          clearAllAttachments(setAttachments);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          showToast.success('Image generated successfully!');
        } else if (jobStatus.status === 'failed') {
          const errorMsg =
            jobStatus.error || jobStatus.message || 'Image generation failed';
          console.error('Job failed with error:', errorMsg);

          clearAllAttachments(setAttachments);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }

          throw new Error(errorMsg);
        } else {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
          await pollJob();
        }
      };

      await pollJob();
    } else if (response.success) {
      const imgs = Array.isArray(response.images) ? response.images : [];

      if (imgs.length === 0) {
        console.warn(
          'generateImage succeeded but returned no images',
          response
        );
        showToast.error(
          'Image generation succeeded but no images were returned'
        );
      }

      const newImages: GeneratedImage[] = imgs.map((imageUrl, idx) => ({
        id: `${response.refId}-${idx}`,
        url: imageUrl,
        prompt: finalPrompt,
        timestamp: new Date(),
        analytics: response.analytics,
        fileName:
          attachments.length > 0
            ? attachments.map((a) => a.fileName)
            : undefined,
      }));

      setGeneratedImages((prev) => [...newImages, ...prev]);
      setSelectedImage(newImages[0]);
      setPrompt('');
      clearAllAttachments(setAttachments);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      showToast.success('Image generated successfully!');
    } else {
      showToast.error('Failed to generate image');
    }
  };

  const handleMaskSubmit = async (maskPrompt: string) => {
    setIsMaskModalOpen(false);
    await performGeneration(maskPrompt);
  };

  const handleDownload = async (image: GeneratedImage) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `generated-image-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
      showToast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      showToast.error('Failed to download image');
    }
  };

  const parseRefAndIndex = (
    id: string
  ): { refId: string; index?: number | undefined } => {
    const lastDash = id.lastIndexOf('-');
    if (lastDash > 0) {
      const suffix = id.substring(lastDash + 1);
      if (/^\d+$/.test(suffix)) {
        const parsedIndex = parseInt(suffix, 10);
        return { refId: id.substring(0, lastDash), index: parsedIndex };
      }
    }
    return { refId: id };
  };

  const handleDeleteClick = (image: GeneratedImage) => {
    setPendingDelete(image);
    setIsConfirmModalOpen(true);
  };

  const handleSelectPrompt = async (image: GeneratedImage) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setSelectedImage({ ...image, url: '' });
    setIsFetchingImages(true);

    try {
      const { refId } = parseRefAndIndex(image.id);
      const images = await api.getGenerationImages(refId);

      if (controller.signal.aborted) return;

      if (images?.[0]) {
        setSelectedImage({ ...image, url: images[0] });
        setGeneratedImages((prev) =>
          prev.map((it) =>
            it.id === image.id ? { ...it, url: images[0] } : it
          )
        );
      } else {
        showToast.error('No images found');
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyErr = err as any;
      if (anyErr?.response) {
        console.error(
          'Failed to fetch images: response status=',
          anyErr.response.status,
          'data=',
          anyErr.response.data
        );
        const respData = anyErr.response.data;
        let msg = 'Failed to load image';
        try {
          if (typeof respData === 'string') {
            const parsed = JSON.parse(respData);
            msg = parsed.message || parsed.error || msg;
          } else if (respData?.message) {
            msg = respData.message;
          } else if (respData?.error) {
            msg = respData.error;
          }
        } catch (parseErr) {
          console.error('Error parsing error response data:', parseErr);
        }
        showToast.error(msg);
      } else {
        console.error('Failed to fetch images:', err);
        showToast.error('Failed to load image');
      }
    } finally {
      if (!controller.signal.aborted) setIsFetchingImages(false);
    }
  };

  const performDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      const { refId, index } = parseRefAndIndex(pendingDelete.id);
      const res = await api.deleteImage({ refId, index });

      if (res && res.success) {
        await loadHistory();
        if (selectedImage?.id === pendingDelete.id) {
          setSelectedImage(null);
        }
        showToast.success('Image deleted');
      } else {
        showToast.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showToast.error('Failed to delete image');
    } finally {
      setIsDeleting(false);
      setIsConfirmModalOpen(false);
      setPendingDelete(null);
    }
  };

  const handleRegenerate = () => {
    if (selectedImage) {
      setPrompt(selectedImage.prompt);
      setSelectedImage(null);
    }
  };

  const bucketSubName = 'image-generator';

  const handleFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    await handleFileSelectionUpload(
      files,
      logout,
      setIsUploading,
      setAttachments,
      () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      },
      bucketSubName
    );
  };

  const removeAttachment = (index: number) => {
    removeAttachmentAtIndex(index, setAttachments);
    if (attachments.length === 1) {
      clearAllAttachments(setAttachments);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      <div className="flex h-full w-full flex-col gap-3 p-3 md:gap-4 md:p-4 lg:flex-row lg:gap-4">
        <div className="flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:h-full lg:w-1/2">
          <div className="flex flex-shrink-0 flex-col border-b border-gray-200 p-3 md:p-4">
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-700 md:text-sm">
                What do you want to create?
              </label>
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                title="Learn how to prompt effectively"
              >
                <Question size={16} weight="bold" />
                <span className="hidden sm:inline">How to Prompt</span>
              </button>
            </div>

            <FileAttachmentDisplay
              attachments={attachments}
              onRemoveAttachment={removeAttachment}
            />

            <div className="relative">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const val = e.target.value;
                  setPrompt(val.length <= 512 ? val : val.slice(0, 512));
                }}
                placeholder="What are you imagining? Limit: 512 Characters"
                maxLength={512}
                className="max-h-[100px] min-h-[70px] w-full resize-none rounded-lg border border-gray-300 p-3 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 md:p-4 md:pr-12"
                disabled={isGenerating || isUploading}
              />
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelection}
                multiple
                accept=".doc,.docx,.jpg,.jpeg,.json,.md,.pdf,.png,.txt"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isGenerating || isUploading}
                className="absolute bottom-2 right-2 rounded-lg p-1.5 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 md:bottom-3 md:right-3"
                title="Attach files"
              >
                <PaperclipHorizontal
                  size={18}
                  className={`rotate-90 ${
                    isGenerating || isUploading
                      ? 'text-gray-300'
                      : 'text-primary-700 hover:text-primary-500'
                  }`}
                />
              </button>
            </div>

            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs text-gray-500">
                {prompt.length > 0 && `${prompt.length} characters`}
              </span>
              <button
                onClick={handleGenerate}
                disabled={
                  isGenerating ||
                  isUploading ||
                  (!prompt.trim() && attachments.length === 0)
                }
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-6 sm:py-3"
              >
                {isUploading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent sm:h-5 sm:w-5" />
                    <span>Uploading...</span>
                  </>
                ) : isGenerating ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent sm:h-5 sm:w-5" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Sparkle size={18} weight="fill" className="sm:hidden" />
                    <Sparkle
                      size={20}
                      weight="fill"
                      className="hidden sm:block"
                    />
                    <span>Generate Image</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {isLoadingHistory ? (
            <div className="flex flex-1 flex-col overflow-hidden p-3 md:p-4">
              <h3 className="mb-2 text-xs font-semibold text-gray-700 md:text-sm">
                Recent Generations
              </h3>
              <div className="flex flex-1 items-center justify-center">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 md:h-8 md:w-8" />
              </div>
            </div>
          ) : (
            generatedImages.length > 0 && (
              <div className="flex flex-shrink-0 flex-col p-3 md:p-4">
                <h3 className="mb-2 text-xs font-semibold text-gray-700 md:text-sm">
                  Recent Generations
                </h3>
                <div className="max-h-48 overflow-y-auto md:max-h-60 lg:max-h-72">
                  {generatedImages.slice(0, 20).map((image) => (
                    <div
                      key={image.id}
                      onClick={() => handleSelectPrompt(image)}
                      className={`flex cursor-pointer items-center justify-between gap-2 border-b px-2 py-1.5 transition-colors hover:bg-gray-50 md:gap-3 md:px-3 md:py-2 ${
                        selectedImage?.id === image.id
                          ? 'bg-primary-50'
                          : 'bg-white'
                      }`}
                    >
                      <div className="min-w-0 flex-1 pr-2">
                        <p className="truncate text-xs text-gray-800 md:text-sm">
                          {image.prompt}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <p className="text-[10px] text-gray-400 md:text-xs">
                          {image.timestamp.toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </div>

        <div className="flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm lg:h-full lg:w-1/2">
          {selectedImage ? (
            <div className="flex h-full flex-col">
              <div className="flex flex-shrink-0 flex-col gap-2 border-b border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between md:gap-3 md:p-4">
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-gray-800 md:text-base">
                    Generated Image
                  </h2>
                  <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 md:text-sm">
                    {selectedImage.prompt}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  <button
                    onClick={handleRegenerate}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 md:px-3 md:py-2 md:text-sm"
                  >
                    <ArrowsClockwise size={16} className="md:hidden" />
                    <ArrowsClockwise size={18} className="hidden md:block" />
                    <span className="hidden sm:inline">Regenerate</span>
                  </button>
                  <button
                    onClick={() => setIsFullScreen(true)}
                    className="rounded-lg border border-gray-300 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 md:p-2"
                    title="View Full Screen"
                  >
                    <ImageIcon size={18} className="md:hidden" />
                    <ImageIcon size={20} className="hidden md:block" />
                  </button>
                  <button
                    onClick={() => handleDownload(selectedImage)}
                    className="rounded-lg border border-gray-300 p-1.5 text-gray-600 transition-colors hover:bg-gray-50 md:p-2"
                    title="Download"
                  >
                    <DownloadSimple size={18} className="md:hidden" />
                    <DownloadSimple size={20} className="hidden md:block" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(selectedImage)}
                    className="rounded-lg border border-red-200 p-1.5 text-red-600 transition-colors hover:bg-red-50 md:p-2"
                  >
                    <Trash size={18} className="md:hidden" />
                    <Trash size={20} className="hidden md:block" />
                  </button>
                </div>
              </div>

              <div className="flex flex-1 items-center justify-center overflow-auto bg-gradient-to-br from-gray-50 to-gray-100 p-2">
                {selectedImage.url ? (
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.prompt}
                    loading="eager"
                    decoding="async"
                    className="max-h-[400px] max-w-full cursor-pointer rounded-lg object-contain shadow-lg lg:max-h-[500px]"
                    onClick={() => setIsFullScreen(true)}
                  />
                ) : isFetchingImages ? (
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 md:h-8 md:w-8" />
                ) : (
                  <p className="text-center text-xs text-gray-500 md:text-sm">
                    No preview available
                  </p>
                )}
              </div>

              <div className="flex-shrink-0 border-t border-gray-200 p-3 md:p-4">
                <div className="grid grid-cols-2 gap-2 text-center md:gap-4">
                  <div>
                    <p className="text-[10px] font-medium text-gray-500 md:text-xs">
                      Cost
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-800 md:text-base">
                      ${selectedImage.analytics?.cost.toFixed(4) || '0.0000'}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-gray-500 md:text-xs">
                      Generated
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-gray-800 md:text-base">
                      {formatDate(selectedImage.timestamp)}{' '}
                      {selectedImage.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-4 text-gray-400">
              <ImageIcon
                size={60}
                weight="thin"
                className="mb-3 opacity-20 md:mb-4 md:hidden"
              />
              <ImageIcon
                size={80}
                weight="thin"
                className="mb-4 hidden opacity-20 md:block"
              />
              <p className="text-center text-gray-500">
                {generatedImages.length === 0 ? (
                  <>
                    <span className="block text-sm font-medium md:text-base">
                      No images yet
                    </span>
                    <span className="mt-1 block text-xs md:text-sm">
                      Generate your first AI image
                    </span>
                  </>
                ) : (
                  <>
                    <span className="block text-sm font-medium md:text-base">
                      Select an image
                    </span>
                    <span className="mt-1 block text-xs md:text-sm">
                      Choose from your recent generations
                    </span>
                  </>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {isFullScreen && selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setIsFullScreen(false)}
        >
          <button
            onClick={() => setIsFullScreen(false)}
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
          >
            <X size={24} />
          </button>
          <img
            src={selectedImage.url}
            alt={selectedImage.prompt}
            loading="eager"
            decoding="async"
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 max-w-2xl -translate-x-1/2 rounded-lg bg-white/10 px-4 py-2 text-center text-sm text-white backdrop-blur-sm">
            {selectedImage.prompt}
          </div>
        </div>
      )}
      <ConfirmationModal
        title="Delete image"
        message={
          pendingDelete
            ? `Are you sure you want to delete this image? This action cannot be undone.`
            : 'Are you sure?'
        }
        isOpen={isConfirmModalOpen}
        loading={isDeleting}
        confirmButtonStyle={{ label: 'Delete', color: 'ERROR' }}
        cancelButtonStyle={{ label: 'Cancel', color: 'NONE' }}
        onConfirm={performDelete}
        onCancel={() => {
          setIsConfirmModalOpen(false);
          setPendingDelete(null);
        }}
      />

      <PromptInstructions
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />

      <MaskModalInput
        isOpen={isMaskModalOpen}
        onClose={() => {
          setIsMaskModalOpen(false);
          setIsGenerating(false);
          // Clear attachments when canceling mask input to allow retry with text-only
          clearAllAttachments(setAttachments);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        onSubmit={handleMaskSubmit}
        isSubmitting={isGenerating}
      />
    </>
  );
};

export default ImageGenerator;
